import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { DataService } from '../services/dataService';
import { PopulationData, HealthInsuranceData, PvCostData, PvSizeData } from '../services/supabase';
import { supabaseLocationService } from '../services/supabaseLocationService';
import { getVMTDataForGHG, clearVMTData } from '../services/vmtDataService';
import { getBuildingsEnergyData, clearBuildingsEnergyCache } from '../services/buildingsEnergyDataService';

export interface LocationData {
  population: PopulationData[];
  healthInsurance: HealthInsuranceData[];
  pvCost: PvCostData[];
  pvSize: PvSizeData[];
  cityCountyMappings: Record<string, string>;
  availableLocations: string[];
}

export interface LocationInfo {
  name: string;
  geofips: number;
  county: string;
  type: 'city' | 'county' | 'state';
}

interface LocationContextType {
  selectedLocation: string;
  selectedGeofips: number | null;
  locationData: LocationData | null;
  loading: boolean;
  error: string | null;
  selectLocation: (location: string) => Promise<void>;
  getCountyForCity: (city: string) => string | null;
  filterLocations: (query: string) => string[];
  getGeofipsForLocation: (locationName: string) => Promise<number | null>;
  coreDataLoading: boolean;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

interface LocationProviderProps {
  children: ReactNode;
}

export const LocationProvider: React.FC<LocationProviderProps> = ({ children }) => {
  const [selectedLocation, setSelectedLocation] = useState<string>(''); // No default location
  const [selectedGeofips, setSelectedGeofips] = useState<number | null>(null);
  const [locationData, setLocationData] = useState<LocationData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [coreDataLoading, setCoreDataLoading] = useState<boolean>(false);

  // Get geofips for a location
  const getGeofipsForLocation = useCallback(async (locationName: string): Promise<number | null> => {
    return await supabaseLocationService.getGeofips(locationName);
  }, []);

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Initialize Supabase location service first
        await supabaseLocationService.initialize();
        
        // Get data from Supabase location service
        const locations = supabaseLocationService.getAvailableLocations();
        const availableLocations = locations.map(loc => loc.name);
        const cityCountyMappings: Record<string, string> = {};
        
        locations.forEach(loc => {
          if (loc.type === 'city') {
            cityCountyMappings[loc.name.toLowerCase()] = loc.county;
          }
        });

        setLocationData({
          population: [],
          healthInsurance: [],
          pvCost: [],
          pvSize: [],
          cityCountyMappings,
          availableLocations
        });

      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load initial data');
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [getGeofipsForLocation]);

  // Load location-specific data
  const loadLocationData = useCallback(async (location: string) => {
    if (!location) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const [population, healthInsurance, pvCost, pvSize] = await Promise.all([
        DataService.getPopulationData(),
        DataService.getHealthInsuranceData(location),
        DataService.getPvCostData(location),
        DataService.getPvSizeData(location)
      ]);

      setLocationData(prev => prev ? {
        ...prev,
        population,
        healthInsurance,
        pvCost,
        pvSize
      } : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load location data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Select location and load data
  const selectLocation = useCallback(async (location: string) => {
    setSelectedLocation(location);
    // Set geofips to null immediately to prevent components from querying with old geofips
    // This prevents race conditions where selectedLocation updates but selectedGeofips hasn't yet
    setSelectedGeofips(null);
    
    try {
      // Get geofips for the selected location
      const geofips = await getGeofipsForLocation(location);
      setSelectedGeofips(geofips);
      
      if (!geofips) {
        console.error('LocationContext - No geofips found for location:', location);
        setError(`No geofips found for location: ${location}`);
        return;
      }
      
      await loadLocationData(location);

      // Preload core datasets (VMT, Electricity, Natural Gas) used by charts/tools
      setCoreDataLoading(true);
      try {
        // Clear any previous VMT cache then repopulate for this location
        clearVMTData();
        clearBuildingsEnergyCache();
        await Promise.all([
          getVMTDataForGHG(location, geofips || undefined).catch(() => []),
          getBuildingsEnergyData(location).catch(() => null)
        ]);
      } finally {
        setCoreDataLoading(false);
      }
    } catch (error) {
      console.error('LocationContext - Error getting geofips for location:', location, error);
      setError(`Failed to get geofips for location: ${location}`);
      setSelectedGeofips(null);
    }
  }, [loadLocationData, getGeofipsForLocation]);

  // Get county for a city
  const getCountyForCity = useCallback((city: string): string | null => {
    return supabaseLocationService.getCountyForCity(city);
  }, []);

  // Filter locations for autocomplete
  const filterLocations = useCallback((query: string): string[] => {
    const locations = supabaseLocationService.searchLocations(query);
    return locations.map(loc => loc.name).slice(0, 10);
  }, []);

  const value: LocationContextType = {
    selectedLocation,
    selectedGeofips,
    locationData,
    loading,
    error,
    selectLocation,
    getCountyForCity,
    filterLocations,
    getGeofipsForLocation,
    coreDataLoading
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};

export const useLocation = (): LocationContextType => {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};

